import { useState, useEffect } from 'react';
import { Cloud, CloudOff, RefreshCw } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { cn } from '../lib/utils';
import { useIsMobile } from '../hooks/useMediaQuery';
import { getSyncStatus, syncOutbox } from '../lib/syncEngine';

/**
 * Sync Status Indicator — shows pending offline changes
 * Only visible on mobile when there are pending items.
 */
export default function SyncIndicator() {
  const isMobile = useIsMobile();
  const [pendingCount, setPendingCount] = useState(0);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    const check = async () => {
      const status = await getSyncStatus().catch(() => ({ pendingCount: 0 }));
      setPendingCount(status.pendingCount);
    };
    check();
    const interval = setInterval(check, 10000); // Check every 10s
    return () => clearInterval(interval);
  }, []);

  const handleSync = async () => {
    if (!navigator.onLine || syncing) return;
    setSyncing(true);
    await syncOutbox();
    const status = await getSyncStatus().catch(() => ({ pendingCount: 0 }));
    setPendingCount(status.pendingCount);
    setSyncing(false);
  };

  if (!isMobile || pendingCount === 0) return null;

  return (
    <AnimatePresence>
      <motion.button
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -10 }}
        onClick={handleSync}
        className={cn(
          "fixed top-14 right-3 z-40 flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-bold shadow-lg transition-all sm:hidden",
          navigator.onLine
            ? "bg-amber-500/90 text-white"
            : "bg-slate-700/90 text-slate-300"
        )}
      >
        {syncing ? (
          <RefreshCw className="w-3.5 h-3.5 animate-spin" />
        ) : navigator.onLine ? (
          <Cloud className="w-3.5 h-3.5" />
        ) : (
          <CloudOff className="w-3.5 h-3.5" />
        )}
        {pendingCount} pending
      </motion.button>
    </AnimatePresence>
  );
}
